import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, useRowContextMenu } from '@medprompt/shared';
import { StatusEnum } from '../enums/statusEnum';

// Which row-level actions a document allows, shared by the Actions column and the right-click
// context menu so they stay in sync.
export const getRowActions = (doc) => {
  const isReady = doc?.status === StatusEnum.READY.value;
  const isFiledAway = doc?.status === StatusEnum.FILED_AWAY.value;
  const isInvalid = doc?.status === StatusEnum.INVALID.value;
  const isPdf = (doc?.fileType || '').toUpperCase() === 'PDF';
  return {
    canSplit: isPdf && isReady,
    canRegenerate: isReady && !isFiledAway,
    canDelete: isReady || isFiledAway || isInvalid,
  };
};

/** Single-document actions from a row: preview, audit trail, split, regenerate, delete, and the context menu. */
const useDocumentRowActions = ({ documents, documentService, refreshDocuments }) => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const { menu: rowContextMenu, close: closeRowContextMenu, handleRowContextMenu } = useRowContextMenu();
  const [previewDocumentId, setPreviewDocumentId] = useState(null);
  const [auditDocument, setAuditDocument] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [documentToRegenerate, setDocumentToRegenerate] = useState(null);
  const [regenerateLoading, setRegenerateLoading] = useState(false);

  const openPreview = useCallback((documentId) => setPreviewDocumentId(documentId), []);
  const closePreview = useCallback(() => setPreviewDocumentId(null), []);

  const openAuditTrail = useCallback((doc) => {
    if (!doc?.documentId) return;
    setAuditDocument(doc);
    // List rows omit assignee history, which the trail uses to name who the document was assigned to.
    documentService.fetchDocumentData(doc.documentId)
      .then((fullDoc) => {
        setAuditDocument(current => (
          current?.documentId === doc.documentId ? fullDoc : current
        ));
      })
      .catch((error) => {
        console.error('Error loading document for audit trail:', error);
      });
  }, [documentService]);
  const closeAuditTrail = useCallback(() => setAuditDocument(null), []);

  const handleDeleteClick = useCallback((documentId) => {
    const docToDelete = documents.find(doc => doc.documentId === documentId);
    if (docToDelete) {
      setDocumentToDelete(docToDelete);
      setDeleteModalOpen(true);
    }
  }, [documents]);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!documentToDelete) return;
    documentService.deleteDocument(documentToDelete.documentId)
      .then(() => {
        refreshDocuments();
        setDeleteModalOpen(false);
        setDocumentToDelete(null);
      })
      .catch(error => {
        console.error('Error deleting document:', error);
        addNotification(`delete-error-${documentToDelete.documentId}`, 'Failed to delete document', { isError: true });
        setDeleteModalOpen(false);
        setDocumentToDelete(null);
      });
  }, [documentToDelete, documentService, refreshDocuments, addNotification]);

  const handleRegenerateClick = useCallback((documentId) => {
    const docToRegenerate = documents.find(doc => doc.documentId === documentId);
    if (docToRegenerate) {
      setDocumentToRegenerate(docToRegenerate);
      setRegenerateModalOpen(true);
    }
  }, [documents]);

  const closeRegenerateModal = useCallback(() => {
    setRegenerateModalOpen(false);
    setDocumentToRegenerate(null);
  }, []);

  const confirmRegenerate = useCallback(() => {
    if (!documentToRegenerate) return;
    setRegenerateLoading(true);
    documentService.regenerateDocument(documentToRegenerate.documentId)
      .then(() => {
        // HTTP_IMMEDIATE strategy: SSE uses SYSTEM_USER_ID so would be filtered out
        addNotification(`regenerate-success-${documentToRegenerate.documentId}`, 'Document queued for regeneration');
        setRegenerateModalOpen(false);
        setDocumentToRegenerate(null);
      })
      .catch(error => {
        console.error('Error regenerating document:', error);
        addNotification(`regenerate-error-${documentToRegenerate.documentId}`, 'Failed to regenerate document', { isError: true });
      })
      .finally(() => {
        setRegenerateLoading(false);
        // Close the modal even if there was an error
        setRegenerateModalOpen(false);
        setDocumentToRegenerate(null);
      });
  }, [documentToRegenerate, documentService, addNotification]);

  // The split page loads the file itself, so a refresh of that route still works.
  const handleSplitExistingDocument = useCallback((doc) => {
    closeRowContextMenu();
    if (!doc?.documentId) return;
    const label = (doc.overrideTitle || doc.documentTitle || 'document').replace(/\.pdf$/i, '');
    navigate(`/split-pdf/${encodeURIComponent(doc.documentId)}`, { state: { label } });
  }, [closeRowContextMenu, navigate]);

  return {
    rowContextMenu,
    closeRowContextMenu,
    handleRowContextMenu,
    previewDocumentId,
    openPreview,
    closePreview,
    auditDocument,
    openAuditTrail,
    closeAuditTrail,
    deleteModalOpen,
    documentToDelete,
    handleDeleteClick,
    closeDeleteModal,
    confirmDelete,
    regenerateModalOpen,
    documentToRegenerate,
    regenerateLoading,
    handleRegenerateClick,
    closeRegenerateModal,
    confirmRegenerate,
    handleSplitExistingDocument,
  };
};

export default useDocumentRowActions;
